import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import FullCalendar from '../../components/calendar/FullCalendar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar } from '../../components/ui/avatar';
import { 
  Clock, 
  DollarSign, 
  FolderKanban, 
  CheckCircle2,
  Play,
  ArrowUpRight,
  Calendar,
  Lock,
  Star,
  TrendingUp,
  Award,
  Mail,
  FileText,
  Phone,
  User,
  CalendarPlus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import QuickNotesWidget from '../../components/widgets/QuickNotesWidget';
import api from '../../lib/axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Day Summary Component - Shows personalized status message
const DaySummary = ({ dashboard, tasks, userName }) => {
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  const getDayMessage = () => {
    const currentTimer = dashboard?.current_timer;
    const pendingTasks = tasks?.filter(t => ['todo', 'pending', 'in_progress'].includes(t.status))?.length || 0;
    const completedToday = dashboard?.tasks_completed_today || 0;
    const hoursToday = dashboard?.hours_today || 0;
    
    // If timer is running
    if (currentTimer) {
      return {
        emoji: '⏱️',
        title: 'Vous êtes en plein travail !',
        message: `Chrono actif sur "${currentTimer.task_name || 'une tâche'}". Continuez comme ça !`,
        color: 'from-emerald-500 to-teal-500',
        bgColor: 'from-emerald-50 to-teal-50'
      };
    }

    // If completed tasks today
    if (completedToday > 0) {
      return {
        emoji: '🎯',
        title: `${completedToday} tâche${completedToday > 1 ? 's' : ''} terminée${completedToday > 1 ? 's' : ''} aujourd'hui !`,
        message: pendingTasks > 0 
          ? `Il vous reste ${pendingTasks} tâche${pendingTasks > 1 ? 's' : ''} en attente.`
          : 'Vous avez terminé toutes vos tâches. Bravo !',
        color: 'from-indigo-500 to-purple-500',
        bgColor: 'from-indigo-50 to-purple-50'
      };
    }

    // If worked today but no completed tasks
    if (hoursToday > 0) {
      return {
        emoji: '💼',
        title: `${hoursToday.toFixed(1)}h de travail aujourd'hui`,
        message: pendingTasks > 0 
          ? `${pendingTasks} tâche${pendingTasks > 1 ? 's' : ''} vous attend${pendingTasks > 1 ? 'ent' : ''}.`
          : 'Continuez votre excellent travail !',
        color: 'from-blue-500 to-cyan-500',
        bgColor: 'from-blue-50 to-cyan-50'
      };
    }

    // If has pending tasks but hasn't started
    if (pendingTasks > 0) {
      return {
        emoji: '📋',
        title: `${pendingTasks} tâche${pendingTasks > 1 ? 's' : ''} vous attend${pendingTasks > 1 ? 'ent' : ''}`,
        message: 'Prêt à démarrer votre journée ? Lancez un chrono pour commencer !',
        color: 'from-amber-500 to-orange-500',
        bgColor: 'from-amber-50 to-orange-50'
      };
    }

    // No tasks assigned
    return {
      emoji: '☕',
      title: 'Journée tranquille',
      message: 'Aucune tâche assignée pour le moment. Profitez-en pour vous former ou proposer votre aide !',
      color: 'from-slate-400 to-slate-500',
      bgColor: 'from-slate-50 to-slate-100'
    };
  };

  const status = getDayMessage();

  return (
    <Card className={cn("card mb-6 overflow-hidden border-0 shadow-sm", `bg-gradient-to-r ${status.bgColor}`)}>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-14 h-14 rounded-2xl flex items-center justify-center text-2xl bg-gradient-to-br shadow-lg",
            status.color
          )}>
            <span className="filter drop-shadow">{status.emoji}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-slate-500 text-sm">{getGreeting()}, {userName} !</span>
            </div>
            <h3 className="font-bold text-lg text-slate-800">{status.title}</h3>
            <p className="text-slate-600 text-sm mt-0.5">{status.message}</p>
          </div>
          {dashboard?.current_timer && (
            <div className="hidden md:block">
              <Badge className="bg-emerald-500 text-white animate-pulse">
                <Clock className="w-3 h-3 mr-1" />
                En cours
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Locked Account Overlay Component (outside main component to avoid re-creation)
const LockedOverlay = ({ creativeStatus }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
    <Card className="max-w-lg mx-4 shadow-2xl border-0">
      <CardContent className="p-8 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-slate-400" />
        </div>
        
        <h2 className="text-2xl font-bold text-slate-800 mb-3">
          Compte en attente de mission
        </h2>
        
        <p className="text-slate-600 mb-6 leading-relaxed">
          Vous devez recevoir une nouvelle mission pour accéder au back-office.
        </p>
        
        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-5 mb-6 text-left">
          <div className="flex items-start gap-3">
            <Award className="w-6 h-6 text-cyan-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-slate-800 mb-1">
                Devenir un créatif intégré
              </h3>
              <p className="text-sm text-slate-600">
                Vous pouvez devenir un créatif intégré avec un contrat sur des missions 
                récurrentes si l&apos;agence vous le propose.
              </p>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-cyan-200/50">
            <p className="text-sm text-slate-600 mb-3">Conditions requises :</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                {(creativeStatus?.progression?.projects_delivered || 0) >= 5 ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                )}
                <span className={cn(
                  (creativeStatus?.progression?.projects_delivered || 0) >= 5 
                    ? "text-emerald-700" 
                    : "text-slate-600"
                )}>
                  Minimum 5 projets livrés ({creativeStatus?.progression?.projects_delivered || 0}/5)
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {(creativeStatus?.progression?.average_rating || 0) >= 17 ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                )}
                <span className={cn(
                  (creativeStatus?.progression?.average_rating || 0) >= 17 
                    ? "text-emerald-700" 
                    : "text-slate-600"
                )}>
                  Note satisfaction ≥ 17/20 ({creativeStatus?.progression?.average_rating || 0}/20)
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                <span className="text-slate-600">
                  Validation par l&apos;agence
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => window.location.href = 'mailto:contact@msc-agence.com'}
          >
            <Mail className="w-4 h-4 mr-2" />
            Contacter l&apos;agence
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600"
            onClick={() => window.location.href = '/profile'}
          >
            <FileText className="w-4 h-4 mr-2" />
            Voir mon profil
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const TeamDashboard = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Creative-specific state
  const [creativeStatus, setCreativeStatus] = useState(null);
  const [isCreative, setIsCreative] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  
  // Manager info
  const [manager, setManager] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dashboardRes, tasksRes, managerRes] = await Promise.all([
        api.get('/dashboard/team-member'),
        api.get('/tasks/my-tasks'),
        api.get('/users/my-manager').catch(() => ({ data: null }))
      ]);
      setDashboard(dashboardRes.data);
      setTasks(tasksRes.data);
      setManager(managerRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const checkCreativeStatus = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await api.get('/missions/creative/${user.id}/status');
      setCreativeStatus(response.data);
      setIsCreative(true);
      setIsLocked(response.data.creative_status === 'locked');
    } catch (error) {
      // User is not a creative partner, that's OK
      setIsCreative(false);
      setIsLocked(false);
    }
  }, [token, user?.id]);

  useEffect(() => {
    fetchData();
    checkCreativeStatus();
  }, [fetchData, checkCreativeStatus]);

  const startTimer = async (taskId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await api.post('/time/start', { task_id: taskId });
      toast.success('Timer démarré !');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Generate weekly data from real time entries or show zeros
  const weeklyData = React.useMemo(() => {
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
    
    return days.map((day, index) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + index);
      // For now, show 0 - will be populated from real data when time tracking is used
      return {
        day,
        hours: 0, // Real data would come from time entries grouped by day
        date: date.toISOString().split('T')[0]
      };
    });
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      todo: 'bg-gray-100 text-gray-700',
      pending: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      review: 'bg-orange-100 text-orange-700',
      awaiting_client: 'bg-amber-100 text-amber-700',
      awaiting_validation: 'bg-amber-100 text-amber-700',
      completed: 'bg-green-100 text-green-700',
      done: 'bg-green-100 text-green-700',
      validated: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = {
      todo: 'À faire',
      pending: 'En attente',
      in_progress: 'En cours',
      review: 'En révision',
      awaiting_client: 'À valider (client)',
      awaiting_validation: 'À valider',
      completed: 'Terminée',
      done: 'Terminée',
      validated: 'Validée',
      cancelled: 'Annulée',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.pending)}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      {/* TimerWidget removed - Now integrated in Header for better UX */}
      
      {/* Locked Overlay for Creative Partners */}
      {isCreative && isLocked && <LockedOverlay creativeStatus={creativeStatus} />}
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64",
        isCreative && isLocked && "blur-md pointer-events-none select-none"
      )}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-[#1a365d]" data-testid="dashboard-title">
              Mon espace de travail
            </h1>
            {isCreative && creativeStatus?.creative_type === 'integre' && (
              <Badge className="bg-emerald-500 text-white">
                <Award className="w-3 h-3 mr-1" />
                Créatif Intégré
              </Badge>
            )}
          </div>
          <p className="text-[#64748b] mt-1">
            Bienvenue, {user?.first_name} !
          </p>
        </div>

        {/* Day Summary Card */}
        <DaySummary 
          dashboard={dashboard}
          tasks={tasks}
          userName={user?.first_name}
        />

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Creative Progress Card (for creative partners) */}
            {isCreative && creativeStatus?.creative_type !== 'integre' && (
              <Card className="card mb-6 border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                        <TrendingUp className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">Progression vers Créatif Intégré</h3>
                        <p className="text-sm text-slate-600">
                          {creativeStatus?.progression?.missing_projects > 0 
                            ? `Encore ${creativeStatus.progression.missing_projects} projet(s) à livrer`
                            : creativeStatus?.progression?.missing_rating_points > 0
                              ? `Améliorer votre note de ${creativeStatus.progression.missing_rating_points} points`
                              : "En attente de validation par l'agence"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <FolderKanban className="w-4 h-4 text-slate-400" />
                          <span className="font-bold text-slate-800">
                            {creativeStatus?.progression?.projects_delivered || 0}/5
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">Projets</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Star className="w-4 h-4 text-amber-400" />
                          <span className="font-bold text-slate-800">
                            {creativeStatus?.progression?.average_rating || 0}/20
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">Note</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="card card-hover" data-testid="stat-projects">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Projets assignés</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {dashboard?.assigned_projects || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#6366f1]/10 rounded-xl flex items-center justify-center">
                      <FolderKanban className="w-6 h-6 text-[#6366f1]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-tasks">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Tâches actives</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {dashboard?.active_tasks || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#f59e0b]/10 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-[#f59e0b]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-hours">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Heures ce mois</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {(dashboard?.hours_this_month || 0).toFixed(1)}h
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#1a365d]/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#1a365d]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-earnings">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Gains ce mois</p>
                      <p className="font-mono text-2xl font-bold text-[#00d4aa]">
                        {(dashboard?.earnings_this_month || 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#00d4aa]/10 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-[#00d4aa]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tasks List */}
              <Card className="card lg:col-span-2" data-testid="tasks-list">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a365d]">
                    Mes tâches
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-[#00d4aa]">
                    Voir tout <ArrowUpRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tasks.slice(0, 5).map((task) => (
                      <div 
                        key={task.id}
                        className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] hover:border-[#00d4aa]/50 transition-all group"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="font-medium text-[#1a365d]">{task.name}</h4>
                            {getStatusBadge(task.status)}
                          </div>
                          <p className="text-sm text-[#64748b]">
                            {task.estimated_hours}h estimées • {task.actual_hours?.toFixed(1) || 0}h réalisées
                          </p>
                        </div>
                        
                        {task.status !== 'completed' && !dashboard?.current_timer && (
                          <Button
                            onClick={() => startTimer(task.id)}
                            size="sm"
                            className="btn-primary opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`start-timer-${task.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Démarrer
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    {tasks.length === 0 && (
                      <div className="text-center py-12 text-[#64748b]">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Aucune tâche assignée</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Activity */}
              <Card className="card" data-testid="weekly-chart">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[#1a365d]">
                    Activité hebdomadaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={weeklyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            background: '#fff', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`${value}h`, 'Heures']}
                        />
                        <Bar 
                          dataKey="hours" 
                          fill="#00d4aa" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#64748b]">Total semaine</span>
                      <span className="font-mono font-bold text-[#1a365d]">
                        {weeklyData.reduce((acc, d) => acc + d.hours, 0).toFixed(1)}h
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Manager Info Widget */}
            {manager && (
              <Card className="card mt-6" data-testid="manager-widget">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Mon responsable
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-4">
                    {manager.avatar_url ? (
                      <img 
                        src={`${BACKEND_URL}${manager.avatar_url}`} 
                        alt={`${manager.first_name} ${manager.last_name}`}
                        className="w-14 h-14 rounded-full object-cover border-2 border-blue-200"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                        {manager.first_name?.[0]}{manager.last_name?.[0]}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-[#1a365d]">
                        {manager.first_name} {manager.last_name}
                      </p>
                      <p className="text-sm text-[#64748b]">
                        {manager.job_title || 'Chef de projet'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    {manager.email && (
                      <a 
                        href={`mailto:${manager.email}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      >
                        <Mail className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                        <span className="text-sm text-slate-600 group-hover:text-blue-600">{manager.email}</span>
                      </a>
                    )}
                    {manager.phone && (
                      <a 
                        href={`tel:${manager.phone}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      >
                        <Phone className="w-4 h-4 text-slate-400 group-hover:text-green-600" />
                        <span className="text-sm text-slate-600 group-hover:text-green-600">{manager.phone}</span>
                      </a>
                    )}
                  </div>
                  
                  {manager.calendar_url && (
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => window.open(manager.calendar_url, '_blank')}
                    >
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      Prendre RDV
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Quick Notes Widget */}
            <div className="mt-6">
              <QuickNotesWidget maxNotes={6} showTitle={true} />
            </div>
            
            {/* My Calendar - Team member sees only their own appointments */}
            <div className="mt-6">
              <FullCalendar theme={theme} user={user} myCalendarOnly={true} />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default TeamDashboard;
